// Connection and sync state. Never colour-only: every state has an icon AND words.
// Loudness follows urgency: offline is a solid warning pill (the loudest), a sync failure is
// danger-tinted, changes waiting to sync get their own (primary) tint, syncing is neutral and
// "all synced" is a quiet success tint.

import {
  CloudAlert,
  CloudCheck,
  CloudOff,
  CloudUpload,
  RefreshCw,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { formatTime } from '../lib/format.js';
import { Button } from './button.js';

/**
 * `unreachable`: the device is online but the server doesn't answer (a warning, quieter
 * than offline: the device itself is fine).
 */
export type SyncStatus = 'online' | 'offline' | 'unreachable' | 'pending' | 'syncing' | 'error';

const SYNC: Record<SyncStatus, { icon: LucideIcon; tone: string }> = {
  online: {
    icon: CloudCheck,
    tone: 'border-state-success/30 bg-state-success-subtle text-state-success',
  },
  offline: {
    icon: WifiOff,
    tone: 'border-state-warning bg-state-warning text-state-warning-foreground',
  },
  unreachable: {
    icon: CloudOff,
    tone: 'border-state-warning/50 bg-state-warning-subtle text-state-warning',
  },
  pending: {
    icon: CloudUpload,
    tone: 'border-primary/40 bg-primary-subtle text-primary-text',
  },
  syncing: { icon: RefreshCw, tone: 'border-border-strong bg-surface-sunken text-fg' },
  error: {
    icon: CloudAlert,
    tone: 'border-state-danger/40 bg-state-danger-subtle text-state-danger',
  },
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The words for a sync state (exported for tests and for places that need text only). */
export function syncLabel(status: SyncStatus, pendingCount = 0): string {
  switch (status) {
    case 'online':
      return 'All changes synced';
    case 'offline':
      return pendingCount > 0
        ? `Offline · ${plural(pendingCount, 'change', 'changes')} saved on this device`
        : 'Offline';
    case 'unreachable':
      return 'Can’t reach the server';
    case 'pending':
      return `${plural(pendingCount, 'change', 'changes')} waiting to sync`;
    case 'syncing':
      return pendingCount > 0
        ? `Syncing ${plural(pendingCount, 'change', 'changes')}…`
        : 'Syncing…';
    case 'error':
      return pendingCount > 0
        ? `Sync failed · ${plural(pendingCount, 'change', 'changes')} not sent`
        : 'Sync failed';
  }
}

export interface SyncIndicatorProps extends Omit<ComponentProps<'div'>, 'children'> {
  status: SyncStatus;
  pendingCount?: number;
  lastSyncedAt?: Date | string;
  /** Shown as a Retry button in the error state. */
  onRetry?: () => void;
  /** Replaces the standard words (e.g. "Connected" where there is nothing to sync). */
  label?: string;
  /**
   * `online` only: plain muted text with a success icon, no pill. For places where "all is
   * well" should not compete with anything (the Admin top bar).
   */
  quiet?: boolean;
}

export function SyncIndicator({
  status,
  pendingCount = 0,
  lastSyncedAt,
  onRetry,
  label,
  quiet = false,
  className,
  ...props
}: SyncIndicatorProps) {
  const { icon: Icon, tone } = SYNC[status];
  const isQuiet = quiet && status === 'online';
  return (
    <div
      role="status"
      aria-live="polite"
      data-status={status}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium comfortable:px-4 comfortable:py-2 comfortable:text-body',
        isQuiet ? 'border-transparent bg-transparent px-1 font-normal text-muted' : tone,
        className,
      )}
      {...props}
    >
      <Icon
        aria-hidden
        className={cn(
          'size-4 shrink-0 comfortable:size-5',
          status === 'syncing' && 'animate-spin-slow',
          isQuiet && 'text-state-success',
        )}
      />
      <span>{label ?? syncLabel(status, pendingCount)}</span>
      {status === 'online' && lastSyncedAt && (
        <span className="font-normal text-fg">· {formatTime(lastSyncedAt)}</span>
      )}
      {status === 'error' && onRetry && (
        <Button
          size="sm"
          variant="ghost"
          // Compact: an inline pill action. Comfortable (stations): a full 48 px touch target.
          className="-my-1 -mr-2 h-7 text-state-danger underline underline-offset-2 hover:bg-state-danger/10 comfortable:-my-2 comfortable:h-touch comfortable:px-4"
          onClick={onRetry}
        >
          Retry
        </Button>
      )}
    </div>
  );
}

export interface OfflineBannerProps extends Omit<ComponentProps<'div'>, 'children'> {
  pendingCount?: number;
  /** When the connection dropped. */
  since?: Date | string;
  action?: ReactNode;
  /**
   * What carries on while offline. Default (stations): changes are saved on this device and
   * sync later. Apps that can't save offline say what they can do instead.
   */
  message?: ReactNode;
}

/** Full-width banner while offline. Work continues; this says so plainly: "Offline since 23:56." */
export function OfflineBanner({
  pendingCount = 0,
  since,
  action,
  message,
  className,
  ...props
}: OfflineBannerProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-state-warning/40 bg-state-warning-subtle px-4 py-2.5 text-state-warning comfortable:py-3.5',
        className,
      )}
      {...props}
    >
      <WifiOff aria-hidden className="size-5 shrink-0" />
      <p className="flex-1 text-body text-fg">
        <strong className="font-semibold text-state-warning">
          {since ? `Offline since ${formatTime(since)}.` : 'You’re offline.'}
        </strong>{' '}
        {message ?? (
          <>
            Keep working: changes are saved on this device
            {pendingCount > 0 ? ` (${plural(pendingCount, 'change', 'changes')} waiting)` : ''} and
            sync when the connection is back.
          </>
        )}
      </p>
      {action}
    </div>
  );
}
